import { Building2, ClipboardCheck, Smartphone } from "lucide-react";
import { scrollToSection } from "@/hooks/useLenis";
import { useContent } from "@/content/ContentContext";
import { Eyebrow, Reveal } from "./Reveal";

const BOXES = [
    {
        icon: Smartphone,
        name: "Effortless for the Customer",
        tagline:
            "Coverage that behaves like the subscriptions they already love.",
        items: [
            "Monthly, quarterly, or annual payments",
            "No large upfront contract price",
            "Everything managed in an online portal",
            "Cancel anytime — no penalty",
        ],
        cta: "Get My Custom Quote",
        action: "quote",
        testid: "plan-card-convenience",
    },
    {
        icon: ClipboardCheck,
        name: "Effortless for the Service Drive",
        tagline:
            "Claims filed and paid online — no adjuster phone tag, no hold music.",
        items: [
            "Online claim filing & payments",
            "Approvals in minutes, at the counter",
            "Retail door rates paid in full",
            "Zero paperwork for your advisors",
        ],
        cta: "Partner With Us",
        action: "dealers",
        testid: "plan-card-service-drive",
    },
    {
        icon: Building2,
        name: "Sold Through Your Dealership",
        tagline:
            "Offered on the showroom floor, in F&I, and in the service lane.",
        items: [
            "A product your store sells itself",
            "Repairs return to your own bays",
            "Recurring revenue every month",
            "Loyalty that outlasts the sale",
        ],
        cta: "Become a Partner Dealer",
        action: "dealers",
        testid: "plan-card-dealership",
    },
];

const CADENCES = ["Monthly", "Quarterly", "Annual"];

export const Plans = ({ onQuote }) => {
    const content = useContent();

    return (
    <section
        id="plans"
        data-testid="plans-section"
        className="relative bg-[#0C1019] py-24 md:py-36"
    >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
                <div>
                    <Eyebrow>{content.plans_eyebrow}</Eyebrow>
                    <h2 className="mt-5 max-w-2xl font-display text-3xl font-bold leading-[1.1] tracking-tight text-white sm:text-4xl lg:text-5xl">
                        {content.plans_heading_a}{" "}
                        <span className="text-sky-400">{content.plans_heading_b}</span>
                    </h2>
                </div>
                <div data-testid="plans-cadence" className="shrink-0">
                    <div className="inline-flex rounded-full border border-white/10 bg-[#121722] p-1.5">
                        {CADENCES.map((c, i) => (
                            <span
                                key={c}
                                className={`rounded-full px-4 py-2 text-xs font-semibold sm:text-sm ${
                                    i === 0
                                        ? "bg-sky-400 text-[#0A0D14]"
                                        : "text-slate-400"
                                }`}
                            >
                                {c}
                            </span>
                        ))}
                    </div>
                    <p className="mt-3 text-xs text-slate-500">
                        Save more when you pay quarterly or annually.
                    </p>
                </div>
            </Reveal>

            <div className="mt-14 grid grid-cols-1 gap-6 md:mt-20 lg:grid-cols-3 lg:gap-8">
                {BOXES.map((box, i) => (
                    <Reveal key={box.name} delay={i * 0.1} className="h-full">
                        <div
                            data-testid={box.testid}
                            className="group flex h-full flex-col rounded-3xl border border-white/10 bg-[#121722] p-8 transition-[border-color,transform,box-shadow] duration-500 hover:-translate-y-2 hover:border-sky-400/40 hover:shadow-[0_0_60px_rgba(56,189,248,0.1)]"
                        >
                            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sky-400/10 text-sky-400 transition-colors duration-300 group-hover:bg-sky-400 group-hover:text-[#0A0D14]">
                                <box.icon className="h-6 w-6" />
                            </span>
                            <h3 className="mt-6 font-display text-2xl font-semibold tracking-tight text-white">
                                {box.name}
                            </h3>
                            <p className="mt-2.5 text-sm leading-relaxed text-slate-400">
                                {box.tagline}
                            </p>
                            <ul className="mt-7 flex flex-1 flex-col gap-3.5 border-t border-white/10 pt-7">
                                {box.items.map((item) => (
                                    <li
                                        key={item}
                                        className="flex items-start gap-3 text-sm text-slate-300"
                                    >
                                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-sky-400" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                            <button
                                data-testid={`${box.testid}-cta-btn`}
                                onClick={
                                    box.action === "quote"
                                        ? onQuote
                                        : () => scrollToSection("#dealers")
                                }
                                className={`mt-9 inline-flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-sm font-bold transition-[background-color,color,box-shadow,transform] duration-300 active:scale-95 ${
                                    box.action === "quote"
                                        ? "bg-sky-400 text-[#0A0D14] hover:bg-sky-300 hover:shadow-[0_0_36px_rgba(56,189,248,0.45)]"
                                        : "border border-white/15 text-white hover:border-sky-400/60 hover:text-sky-300"
                                }`}
                            >
                                {box.cta}
                            </button>
                        </div>
                    </Reveal>
                ))}
            </div>

            <Reveal delay={0.15}>
                <p
                    data-testid="plans-pricing-note"
                    className="mx-auto mt-12 max-w-2xl text-center text-sm leading-relaxed text-slate-500"
                >
                    {content.plans_note}
                </p>
            </Reveal>
        </div>
    </section>
    );
};
